/*
      ___           ___
     /\  \         /\  \
    /::\  \       /::\  \
   /:/\:\  \     /:/\ \  \
  /::\ \:\  \   _\:\ \ \  \
 /:/\:\ \:\__\ /\ \:\ \ \__\
 \/_|::\/:/  / \:\ \:\ \/__/
    |:|::/  /   \:\ \:\__\
    |:|\/__/     \:\/:/  /
    |:|  |        \::/  /
     \|__|         \/__/

    Developed by Ralph Shippam - 2019.
*/

import {
    AfterContentInit, AfterViewInit,
    Component,
    ComponentFactoryResolver,
    ComponentRef,
    ContentChild,
    ContentChildren,
    Directive,
    DoCheck,
    ElementRef,
    EmbeddedViewRef,
    EventEmitter,
    HostListener,
    Inject,
    Injector,
    Input,
    OnChanges,
    OnDestroy,
    OnInit,
    Optional,
    Output,
    QueryList,
    Self,
    SimpleChanges,
    ViewChildren,
    ViewContainerRef,
    ViewEncapsulation
} from '@angular/core';
import {FormGroupDirective, NgControl, NgForm} from '@angular/forms';
import {BehaviorSubject, Subject} from 'rxjs';
import {MatSelect} from '@angular/material/select';
import {MatFormFieldControl} from '@angular/material/form-field';
import {RSValidatorService} from './rsvalidator.service';

// Simple validation handler to inject error and hint blocks rather than
// manually creating FormGroups, watch for changes and toggle error states

// Conforms to our validation service provider (used on server side types)
// TODO: Note, this could be rewritten to extend the MatFormFieldControl with our inputs too. This would work better meaning we do not step outside of our 'sandbox' to find the FormField refs
// TODO: Note, could move this to the mat-form-field?

@Directive({
    selector: '[rsValid]'
})
// TODO: Implement form ng-pristine && ng-dirty?
// TODO: Implement form and form group controls?
// TODO: Implement Aria

export class RSValidatorDirective implements MatFormFieldControl<any>,
                                  OnInit, OnChanges, OnDestroy, DoCheck,
                                  AfterViewInit, AfterContentInit {

    @Input() rsValid: string;
    @Input() rsValidValue: string;
    @Input() rsValidParameters?: object;
    @Input() rsValidHint: string;
    @Input() rsValidName: string;
    @Input() rsValidWarn?: string;

    @Input() rsValidError: string; // TODO: IMPLEMENT - Hacky for now, send 'false' to hide the error but keep the validation
    @Input() rsValidErrorOnBlank: boolean;
    @Input() rsValidOnceInteracted: boolean;
    // @Input() rsValidOnceBlur: boolean;

    @Input() placeholder = '';

    // Watch the model
    @Input() set ngModel(aValue) {
        this._value = aValue;
        this.ngOnChanges({});
    }

    element: HTMLInputElement;
    component: ComponentRef<any>;
    formField: HTMLElement;
    stateChanges = new Subject<void>();
    error: string;
    errorState: boolean;
    id: string; // Unused hereafter
    hasBlur: boolean;
    focused: boolean;
    shouldLabelFloat: boolean;
    required: boolean;
    private _updateErrorStatus;

    get tabIndex() {
        // TODO: HOW DOES ANGULAR HANDLE TABINDEX?
        return this.elementRef.nativeElement.tabIndex;
    }

    /** The input element's value / attributes. */
    private _value: string;
    get value() {
        // If we have an _value, we've been set or ngModelled which we trust for Angular fields. Non angular fields (non bound) will work with below
        if (typeof this.rsValidValue !== 'undefined') { // TODO: TEST CASE THIS, WHAT IF THE SUPPLIED VALUE IS UNDEFINED? CAN WE TEST IF AN @INPUT HAS BEEN ATTACHED?
            return this.rsValidValue;
        } else if (this._value) {
            return this._value;
        } else if (this.elementRef.nativeElement.nodeName === 'MAT-CHECKBOX') {
            // Get Associated input
            return this.elementRef.nativeElement.children[0].children[0].children[0].checked; // TODO: IMPROVE WITH A SEEK! THIS IS A LITTLE TOO RIGID
        } else if (this.ngControlSelect) {
            return this.ngControlSelect.value;
        } else if (this.ngControl) {
            return this.ngControl.value;
        } else if (this.elementRef.nativeElement.value) {
            return this.elementRef.nativeElement.value;
        } else {
            return this._value;
        }
    }
    set value(value: string) {
        this._value = value; // Fallback for unsupported types

        if (this.elementRef.nativeElement.nodeName === 'MAT-CHECKBOX') {
            this.elementRef.nativeElement.children[0].children[0].children[0].checked = !!value;
        } else {
            if (value !== this.value) {
                this.elementRef.nativeElement.value = value;
            }
        }

        // Update the status
        this.ngOnChanges({});
    }
    get disabled() { return this.element.disabled ? this.element.disabled : false; }
    get empty(): boolean {
        // TODO: TEST CASES FOR THIS (i.e. DATE)
        return false;
    }

    constructor(public elementRef: ElementRef,
                public viewContainerRef: ViewContainerRef,
                @Self() @Optional() public ngControl: NgControl,
                @Self() @Optional() public ngControlSelect: MatSelect,
                @Optional() private parentForm: NgForm,
                @Optional() private parentFormGroup: FormGroupDirective,
                @Inject(ComponentFactoryResolver) public factoryResolver) {
        this.factoryResolver = factoryResolver;
    }

    static seekParentWithClass(element: HTMLElement, className: string): HTMLElement | undefined {
        let parentElement: HTMLElement;
        while (element.parentElement && !parentElement) {
            const elementsFound = element.parentElement.classList.contains(className);
            if (elementsFound) {
                parentElement = element.parentElement as HTMLElement;
            }
            element = element.parentElement;
        }
        return parentElement;
    }

    static seekSiblingWithClass(element: HTMLElement, className: string): HTMLElement | undefined {
        let parentElement: HTMLElement;
        while (element.parentElement && !parentElement) {
            const elementsFound = element.parentElement.getElementsByClassName(className);
            if (elementsFound.length) {
                parentElement = elementsFound[0] as HTMLElement;
            }
            element = element.parentElement;
        }
        return parentElement;
    }

    ngOnInit() {
        // Check we can be used
        const type = this.elementRef.nativeElement.nodeName;
        console.log('RSValidatorDirective::: InitOn:: ' + type);
        if (type === 'TEXTAREA' || type === 'INPUT' || type === 'SELECT' || type === 'MAT-SELECT' || type === 'MAT-CHECKBOX') {
            if (type !== 'TEXTAREA' && INPUT_INVALID_TYPES.indexOf(this.elementRef.nativeElement.type) !== -1) {
                console.error('Unsupported Validation Type: ' + this.elementRef.nativeElement.type);
                return;
            } else if (type === 'MAT-SELECT') {
                // Custom CDK implementation
                // @HostListener('valueChange') below
            } else if (type === 'MAT-CHECKBOX') {
                // TODO: IMPLEMENT
            }
        } else {
            // Unsupported Node;
            console.error('Unsupported Validation Node: ' + type);
            return;
        }

        // Dynamically add the form validation error message and hint blocks;
        const factory = this.factoryResolver.resolveComponentFactory(RSValidErrorComponent);
        this.component = this.viewContainerRef.createComponent(factory);
        this.component.changeDetectorRef.detectChanges(); // update it's view
        this.element = (this.component.hostView as EmbeddedViewRef<any>).rootNodes[0] as HTMLInputElement;
        this.formField = RSValidatorDirective.seekParentWithClass(this.element, 'mat-form-field');

        // Seek for the hint wrapper and append our component
        const wrapper = RSValidatorDirective.seekSiblingWithClass(this.element, 'mat-form-field-subscript-wrapper');
        if (typeof wrapper !== 'undefined') {
            wrapper.appendChild(this.element);
        } else {
            // TODO: What happened here?
            this.component.destroy();
            return;
        }

        // defaults
        this.rsValidOnceInteracted = (typeof this.rsValidOnceInteracted === 'undefined') ? false : !!this.rsValidOnceInteracted; // Only validate once we've edited
        // this.rsValidOnceBlur =       (typeof this.rsValidOnceBlur === 'undefined') ? true : !!this.rsValidOnceBlur; // Only validate once we've left the field
        this.rsValidErrorOnBlank =   (typeof this.rsValidErrorOnBlank === 'undefined') ? false : !!this.rsValidErrorOnBlank;
        // if (this.rsValidOnceInteracted && this.rsValidOnceBlur) { this.rsValidOnceInteracted = false; }

        this.elementRef.nativeElement.classList.add('ng-pristine'); // Used for our implementation of FormGroup validation to ensure compatibility
        if (this.formField) {
            this.formField.classList.add('rsValid');
        }

        // TODO: SHOULD WE FALL BACK TO PLACEHOLDER?
        this.rsValidName = this.placeholder || 'Input';

        // Force sync of changes
        this.ngOnChanges({});
    }

    ngAfterViewInit () {
        this.ngOnChanges({});
    }

    ngAfterContentInit() {
        this.ngOnChanges({});
    }

    // Trial to remove this as it is really heavy!!!
    ngDoCheck() {
    //    if (this.formField && this.element) {
    //        this.updateErrorStatus();
    //    }
    }

    ngOnChanges(changes: SimpleChanges) {
        // Update the directive properties on scope
        for (const k in changes) {
            if (changes.hasOwnProperty(k)) {
                this[k] = changes[k].currentValue;
            }
        }

        // Update bound component;
        if (typeof this.component !== 'undefined' && this.component.instance) {
            this.component.instance.hint$ = this.rsValidHint;
        }

        // Trigger status check but debounce the request
        if (this._updateErrorStatus) {
            clearTimeout(this._updateErrorStatus);
        }
        this._updateErrorStatus = setTimeout(() => this.updateErrorStatus(), 200);
    }

    ngOnDestroy() {
        try { this.component.destroy(); } catch (e) {}
        this.stateChanges.complete();
    }

    // We currently do not benefit from ngTouched and ngDirty notifiers if we are outside of a formgroup. Current simple lightweight for now to ensure we do not miss this notification;
    @HostListener('keydown', ['$event'])
    @HostListener('keyup', ['$event'])
    @HostListener('keypress', ['$event'])
    @HostListener('change', ['$event'])
    @HostListener('click', ['$event'])
    @HostListener('blur', ['$event'])
    @HostListener('focus', ['$event'])
    onEvent(event) {
        if (!this.elementRef.nativeElement.classList.contains('ng-touched')) {
            this.elementRef.nativeElement.classList.add('ng-touched');
        }
        if (['keydown', 'keyup', 'keypress', 'change', 'blur'].indexOf(event.type) !== -1) {
            if (this.elementRef.nativeElement.classList.contains('ng-pristine')) {
                this.elementRef.nativeElement.classList.remove('ng-pristine');
            }
        }
        // Track when user has visited and left the field
        // TODO: USE TYPEOF. constructor.name IS NOT AVAIL AFTER NG BUILD
        if (event.type === 'blur' || event.constructor.name === 'MatCheckboxChange' // Toggle blur as soon as anything happens with a checkbox
        ) {
            this.hasBlur = true;
        }
    }

    // Listen for MAT-SELECT value change events
    // TODO: THIS IS NOT TRIGGERING WHEN THE INITIAL BOUND VALUE IS SET, SO WE HAVE NO SIGHT OF UNCHANGED VALUES? HAVE ADDED A ngControlSelect to get the native element for the value. Should probably wrap everyything in a consistent wrapper
    @HostListener('selectionChange', ['$event'])
    @HostListener('valueChange', ['$event'])
    valueChange (event) {
        this.value = event; // Retrofit the value back into the MAT-SELECT
        this.onEvent(new Event('change')); // Trigger the standard process like we're an input
    }

    public isValid() {
        return !RSValidatorService.invalidTypeOf(this.value, this.rsValid, this.rsValidName, this.rsValidParameters);
    }

    public showError() {
        // Set the field to 'visited' to toggle errors
        this.hasBlur = true;
        // this.rsValidOnceBlur = false;
        this.updateErrorStatus(true);
    }

    public focus() {
        if (this.elementRef.nativeElement.focus) {
            this.elementRef.nativeElement.focus();
        } else if (this.elementRef.nativeElement.scrollIntoView) {
            this.elementRef.nativeElement.scrollIntoView();
        }
    }

    private updateErrorStatus(bForceError: boolean = false) {
        if (!this.formField || !this.component || !this.component.instance) {
            // Come back and try again
            this._updateErrorStatus = setTimeout(() => this.updateErrorStatus(), 200);
            return;
        }

        const value = this.value;
        let error, warn;

        if (this.rsValid) {
            error = RSValidatorService.invalidTypeOf(value, this.rsValid, this.rsValidName, this.rsValidParameters);

            // Touched = Item or form interaction
            // Pristine = Input / interaction
            // TODO: CONFIRM OUR NEEDS HERE, DO WE WANT TO ERROR AS THEY TYPE, IF NOT, PRISTINE WILL NOT WORK FOR US?
            if (!bForceError &&
                (typeof value === 'undefined' || value === null || value === '') && // Only allow us to fall through when we do not have a value. Default values otherwise are shown as okay?
                this.rsValidOnceInteracted && // Only bypass when they want us to
                !this.hasBlur
            ) {
                error = undefined;
            } else if (error && typeof this.rsValidError === 'string' && this.rsValidError !== 'false') {
                // Do we have a custom error form the UI?
                error = this.rsValidError;
            }
        }

        // Remove where we use RSValidError to overwrite the error (with false)
        if (typeof this.rsValidError !== 'undefined' && this.rsValidError !== null && this.rsValidError.toString() === 'false') {
            error = '';
        }

        // Declare the error & toggle the error state
        this.error = (typeof error !== 'undefined' ? error.toString() : undefined);
        this.errorState = (typeof error !== 'undefined');
        if (!this.errorState && this.rsValidWarn) {
            warn = RSValidatorService.invalidTypeOf(value, this.rsValidWarn, this.rsValidName, this.rsValidParameters);
        }

        // Update our trackers
        this.toggleError(this.error, warn, bForceError);
    }

    private toggleError(error?: string, warning?: string, bForcePush: boolean = false) {
        let bDidChange = false;

        // mat-form-field-invalid -> mat-form-field
        if (typeof error !== 'undefined') {
            bDidChange = bDidChange || this.component.instance.error$ !== error;
            this.component.instance.error$ = error;
            this.formField.classList.add('mat-form-field-invalid');

            // Remove the warning
            bDidChange = bDidChange || this.component.instance.warn$ !== undefined;
            this.component.instance.warn$ = undefined;
            this.formField.classList.remove('mat-form-field-warning');

        } else if (typeof warning !== 'undefined') {
            bDidChange = bDidChange || this.component.instance.warn$ !== warning;
            this.component.instance.warning$ = warning;
            this.formField.classList.add('mat-form-field-warning');

            // Remove the error
            bDidChange = bDidChange || this.component.instance.error$ !== undefined;
            this.component.instance.error$ = undefined;
            this.formField.classList.remove('mat-form-field-invalid');
        } else {
            bDidChange = bDidChange || this.component.instance.error$ !== undefined;
            this.component.instance.error$ = undefined;
            bDidChange = bDidChange || this.component.instance.warn$ !== undefined;
            this.component.instance.warning$ = undefined;
            this.formField.classList.remove('mat-form-field-warning');
            this.formField.classList.remove('mat-form-field-invalid');
        }

        // Push
        if (bDidChange || bForcePush) {
            // Warn of changes
            this.stateChanges.next();
            this.component.changeDetectorRef.detectChanges();
        }
    }

    //  Some unused implementations
    setDescribedByIds(ids: string[]) { }
    onContainerClick() {}
}

// Invalid input type.
const INPUT_INVALID_TYPES = [
    'button',
    'checkbox',
    'color',
    'file',
    'hidden',
    'image',
    'radio',
    'range',
    'reset',
    'submit'
];

/*
TODO: BUILD A COMPONENT / DIRECTIVE THAT JUST SHOWS THE MESSAGE WITHOUT MODEL BINDING & INPUTS? MIGHT BE EASIER FOR MAT SLIDERS AND THE LIKES
@Directive({
    selector: 'rsValidatorMessage'
})
export class RSValidatorMessageDirective implements OnInit, OnChanges, OnDestroy {
    @Input() rsValid: string;
    @Input() rsValidValue?: string;
    @Input() rsValidParameters?: object;
    @Input() rsValidHint: string;
    @Input() rsValidName: string;
    @Input() rsValidWarn?: string;

    @Input() rsValidError: string; // TODO: IMPLEMENT - Hacky for now, send 'false' to hide the error but keep the validation
    @Input() rsValidErrorOnBlank: boolean;
    @Input() rsValidOnceInteracted: boolean;
    @Input() rsValidOnceBlur: boolean;

    constructor() {}
    ngOnChanges(changes: SimpleChanges): void {}
    ngOnInit(): void {}
    ngOnDestroy(): void {}
}*/


@Component({
    selector: 'rsValidError',
    template: `
        <ng-container [ngSwitch]="iMode$">
            <mat-hint   *ngSwitchCase="0"                  >{{hint$}}</mat-hint>
            <mat-hint   *ngSwitchCase="1" class="warning"  >{{warning$}}</mat-hint>
            <mat-error  *ngSwitchCase="2"                  >{{error$}}</mat-error>
        </ng-container>
    `,
    encapsulation: ViewEncapsulation.None
})
export class RSValidErrorComponent {
    private _error: string;
    public set error$(sValue: string) {
        if (this._error !== sValue) {
            this._error = sValue;
            this.scheduleToggleState();
        }
    }
    public get error$() {
        return this._error;
    }
    private _hint: string;
    public set hint$(sValue: string) {
        if (this._hint !== sValue) {
            this._hint = sValue;
            this.scheduleToggleState();
        }
    }
    public get hint$() {
        return this._hint;
    }
    private _warning: string;
    public set warning$(sValue: string) {
        if (this._warning !== sValue) {
            this._warning = sValue;
            this.scheduleToggleState();
        }
    }
    public get warning$() {
        return this._warning;
    }
    private _toggleStateTimeout;

    // Store the states statically to avoid angular zone warnings
    public iMode$ = 0; // -1 = nada, 0 = hind, 1 = warn, 2 = error

    scheduleToggleState() {
        if (this._toggleStateTimeout) {
            clearTimeout(this._toggleStateTimeout);
        }
        // Debounce requests
        this._toggleStateTimeout = setTimeout(() => this.toggleState(), 100);
    }

    public toggleState() {
        if (this.showError()) {
            this.iMode$ = 2;
        } else if (this.showWarn()) {
            this.iMode$ = 1;
        } else if (this.showHint()) {
            this.iMode$ = 0;
        } else {
            this.iMode$ = -1;
        }

        // Clear the timer
        // this._toggleStateTimeout = false;
    }

    showHint() {
        return !!(!this.showError() && !this.showWarn() && this.hint$ && this.hint$.length);
    }
    showError() {
        return !!(this.error$ && this.error$.length);
    }
    showWarn() {
        return !!(!this.showError() && this.warning$ && this.warning$.length);
    }
}

// Tiny selector to identify primary form buttons
@Directive({ selector: '[rsFormSubmit]' })
export class RSValidatorFormSubmitDirective {
    constructor(public elementRef: ElementRef) {}
}

// Handler for sub-component elements
// Simple form handler to validate all child entities before submitting a form!
// NOTE: WE CANNOT HAVE A PARENT DIRECTIVE THAT SEEKS FOR CHILDREN DUE TO ANGULAR DESIGN;
// https://github.com/angular/angular/issues/10121
/*@Directive({
    selector: '[rsValidSubForm]',
})
// ATTEMPT 2 TRIAL EXTENDING THE CLASS WITH THIS (TO SAVE DUPLICATING CODE) THEN SELECT THIS?? WORKS BUT MESSY.
// ATTEMPT 3, Failed.. as above
export class RSValidatorSubFormChildren implements AfterContentInit {
    @ContentChildren(RSValidatorDirective, {descendants: true}) private _contentChildren !: QueryList<RSValidatorDirective>;
    @ContentChildren(RSValidatorDirective, {descendants: true}) private __ContentChildren !: QueryList<RSValidatorDirective>;
    @ContentChildren(RSValidatorDirective)                      private ___ContentChildren !: QueryList<RSValidatorDirective>;
    @Output('contentChildren') contentChildren = new QueryList<RSValidatorDirective>();

    ngAfterContentInit () {
        setInterval(() => {
            console.log(this);
        }, 1000);
        this._contentChildren.changes.subscribe((value => this.contentChildren = this._contentChildren));
    }

    isValid () {
        return false;
    }
}*/

// Reusable class extension for subviews using the RSValidatorSubForm
@Component({
    selector: 'rs-validator-sub-form-class',
    template: '<p>RSValidatorSubFormClass</p>'
})
export class RSValidatorSubFormClass {
    @ViewChildren(RSValidatorDirective) contentChildren !: QueryList<RSValidatorDirective>;

    private _inputs: any;
    set inputs(value: any) {
        this._inputs = value;
        if (this._values) {
            this._values.next(this.inputs);
        }
    }
    get inputs() {
        return this._inputs;
    }

    // Subscribable
    private _values: BehaviorSubject<any> = new BehaviorSubject(this.inputs);
    values = this._values.asObservable();
}

@Directive({
    selector: 'rsValidSubForm',
    exportAs: 'rsValidSubForm'
})
export class RSValidatorSubFormDirective implements OnInit, AfterViewInit, OnChanges {
    @Input('inputs') private inputs: any;
    @Input('component') private component: any;
    private componentLoaded: string;
    public componentRef: ComponentRef<any>; // Shared in case anyone needs to communicate with their subforms
    private viewDidInit: boolean;

    public contentChildren !: QueryList<RSValidatorDirective>; // NOTE; CONTENTCHILDREN CANNOT SELECT WITHIN THE CHILDCOMPONENT

    constructor(
        private _componentFactoryResolver: ComponentFactoryResolver,
        private _container: ViewContainerRef,
        private _injector: Injector
    ) {}

    ngAfterViewInit(): void {
        this.viewDidInit = true;
        this.renderComponent(); // Trigger us to redraw the component now we've loaded
    }

    removeComponent() {
        // Is there a component in view already?
        if (this.componentRef) {
            // Destroy the former
            try { this.component.hostView.detachView(this.componentRef.hostView);    } catch (e) {}
            try { this.component.hostView.clear();                                   } catch (e) {}
            try { this.componentRef.destroy();                                       } catch (e) {}
            try { this.component.destroy();                                          } catch (e) {}
        }
    }

    renderComponent() {
        // Load the component
        if (this.component && this.viewDidInit) {
            if (this.component.name === this.componentLoaded) {
                // Same same, leave them
                // Trigger the change handler though
                this.componentRef.instance.inputs = this.inputs;
                // this.componentRef.changeDetectorRef.detectChanges(); // update it's view
            } else {
                // Tidy up existing component
                this.removeComponent();

                // Build the component
                this.componentRef = this._componentFactoryResolver.resolveComponentFactory(this.component).create(this._injector);

                // Pass the data along to the instance!
                this.componentRef.instance.inputs = this.inputs;

                // Attach component to the appRef so that it's inside the ng component tree
                this._container.insert(this.componentRef.hostView);

                // Save the current component
                this.componentLoaded = this.component.name;

                // Trigger the change control
                this.componentRef.changeDetectorRef.detectChanges();

                // Watch Children
                if (this.componentRef.instance.contentChildren) {
                    const fAssignChildren = () => {
                        this.contentChildren = this.componentRef.instance.contentChildren;
                    };
                    this.componentRef.instance.contentChildren.changes.subscribe(fAssignChildren);
                    fAssignChildren();
                }
            }
        } else {
            // Tidy up any existing component
            this.removeComponent();
        }
    }

    ngOnChanges() {
        // Render the component. This will wait for the view to init
        this.renderComponent();
    }

    ngOnInit() {}

}

@Directive({
    selector: 'rsValidForm',
    host: {
        '[style.display]': '"block"',
    },
    exportAs: 'rsValidForm'
})
// Simple form handler to validate all child entities before submitting a form!
export class RSValidatorFormDirective implements AfterContentInit {
    @ContentChildren(RSValidatorDirective, {descendants: true}) _contentChildren !: QueryList<RSValidatorDirective>;
    @ContentChildren(RSValidatorSubFormDirective, {descendants: true}) _contentSubForms !: QueryList<RSValidatorSubFormDirective>;
    @ContentChild(RSValidatorFormSubmitDirective) submitButton !: RSValidatorFormSubmitDirective;
    @Output('submit') submitMethod: EventEmitter<any> = new EventEmitter();

    contentChildren: Array<RSValidatorDirective> = [];

    @Input() rsValidOnceInteracted: boolean; // Global override for form fields
    // @Input() rsValidOnceBlur: boolean; // Global override for form fields retired in favour of rsValidOnceInteracted
    private _rendered: boolean;

    constructor () {
        this._rendered = false;
    }

    /*
    TODO: IMPLEMENT ENTER TO TAB THROUGH A FORM AND SUBMIT ON LAST ITEM
          - CURRENTLY UNABLE TO ITERATE OVER A CHECKBOX

    TODO: COMPONENT STRUCTURE NOW BREAKS THIS CODE!! THE ENTER KEY WILL JUMP OVER COMPONENTS!!!!!!!
    */
    @HostListener('keypress', ['$event'])
    keyPress (event) {
        if (event.key === 'Enter' && event.srcElement.nodeName !== 'TEXTAREA' && event.srcElement.nodeName !== 'BUTTON') {

            // Prevent
            event.preventDefault();

            // Iterate over the items to find which has focus;
            let bLocated = false, bTriggered = false;
            this.contentChildren.forEach((field) => {

                // We are focused on this element currently;
                if (event.path.indexOf(field.elementRef.nativeElement) !== -1) {
                    bLocated = true;
                } else if (bLocated && !bTriggered) {
                    bTriggered = true;
                    field.focus(); // TODO: DOES NOT WORK ON MAT-CHECKBOX WITHIN OUR FORM FIELD
                } else {
                    // Already sorted.
                }
            });

            // We found the item but nothing within our elements appears afterwards. Therefore lets submit the form if one has been nominated;
            if (bLocated && !bTriggered) {
                if (this.submitButton) {
                    if (this.submitButton.elementRef.nativeElement.click) { this.submitButton.elementRef.nativeElement.click(); }
                } else if (this.submitMethod) {
                    this.submitMethod.emit();
                }
            }
        }
    }

    ngAfterContentInit() {
        // Allow the validation routine to update the UI only once rendered
        setTimeout(() => this._rendered = true);

        const fAssignProperties = () => {
            // Build the content children based on inputs
            this.contentChildren = [];

            // TODO: ADD IN THE PARENT ONES. ORDERING????????????? THIS IS IMPORTAN TO ALLOW OUR TABBING TO WORK.
            // TODO: ADD TABINDEX AS AN OUTPUT? THEN STEP EACH TAB OF THE DIRECTIVE V.S.
            //       THE CONTROL?? REQUIRES UNDERSTANDING OF WHAT ANGULAR DOES TO CALCULATE THIS
            this._contentSubForms.forEach((oForm) => {
                if (oForm.contentChildren) {
                    this.contentChildren = this.contentChildren.concat(oForm.contentChildren.toArray());
                }
            });

            // Add in native elements
            if (this._contentChildren) {
                this.contentChildren = this.contentChildren.concat(this._contentChildren.toArray());
            }

            this.contentChildren.forEach((field) => {
                // Cascade the default value to fields
                // field.rsValidOnceBlur = !!this.rsValidOnceBlur;
                field.rsValidOnceInteracted = !!this.rsValidOnceInteracted;
                field.ngOnChanges({}); // Force the field to check it's status
            });
        };

        const fWatchSubForms = () => {
            // NOTE: THIS IS DUE TO COMPONENT SANDBOXING, WE HAVE NO WAY TO SEEK GRANDCHILDREN
            this._contentSubForms.forEach((oForm) => {
                // Watch the child form elements;
                if (oForm.contentChildren) {
                    oForm.contentChildren.changes.subscribe(fAssignProperties);
                    fAssignProperties();
                } else {
                    // Allow the view to init as we need to watch this
                    setTimeout(fWatchSubForms, 250);
                }
            });
        };

        // Watch for form item changes and update them
        // this.contentChildren.changes.subscribe((value => fAssignProperties()));
        this._contentChildren.changes.subscribe(fAssignProperties);
        this._contentSubForms.changes.subscribe(fWatchSubForms);

        // Update children with any inherent properties
        fAssignProperties();
        fWatchSubForms();
    }

    isValid(bPrompt: boolean = false) {
        let bValid = true, bDidFocus = false;
        if (this.contentChildren && this.contentChildren.length && this._rendered) {
            this.contentChildren.forEach((field: RSValidatorDirective) => {
                if (!field.isValid()) {
                    bValid = false;

                    if (bPrompt) {
                        // Toggle Error
                        field.showError();

                        // Focus
                        if (!bDidFocus) {
                            bDidFocus = true;
                            field.focus();
                        }
                    }
                }
            });
        }

        // If they want to prompt, the form has been submitted. So everything is 'interacted'
        if (bPrompt && this.rsValidOnceInteracted) {
            this.rsValidOnceInteracted = false;
            this.ngAfterContentInit(); // Cascade the new value
        }

        return bValid;
    }
}

